// The Machines List view's page as pure data (#6561, epic #6552): six machines a page, the
// page read off the url (owner ruling G10), clamped on read, and the rule that sends the
// list back to page one when it is asked a new question. `FleetMachines` renders it under
// the one numbered `<Pager/>`.
//
// ONE WINDOW, ONE CLAMP. The nav is `serverPageNav` (`./paginate.ts`) and the numbered
// buttons are the `<Pager/>`'s `pageWindow`, the window every numbered pager here draws: first, last, the current page +/- 1, a gap between. At seven
// pages or fewer it lists every page, where the prototype draws `1 2 ... 4`; that is a
// listed deviation, not a second window (#6561 executor note 1).
//
// ONE CUT. A page is `pageGroups` (`./fleet-machines.ts`) read from the page's first row:
// the same cut the Cards view makes, so a page that starts or ends inside a server's group
// keeps that group's header.
//
// THE PAGE RESETS ON A NEW QUESTION, NOT ON NEW DATA. A change of filter, search, sort,
// direction, grouping or view goes back to page one (`pageAfter`, applied by the one url
// write, `useFleetView`); the 20-second poll keeps the page, and a page the poll's rows no
// longer reach clamps to the last one on read.

import { z } from 'zod';
import {
  type FleetViewState,
  type MachineList,
  type PagedGroup,
  pageGroups,
} from './fleet-machines';
import { type PageNav, type ServerPageNav, serverPageNav } from './paginate';

/** Machines a List view page shows (the prototype's `PER`). */
export const LIST_PAGE = 6;

// A plain positive integer: no sign, no leading zero, no fraction, no space, and short
// enough that no real fleet's page count is cut off.
const PageSchema = z
  .string()
  .regex(/^[1-9]\d{0,5}$/)
  .transform(Number)
  .catch(1);

/** The page off `useSearchParams`' value; anything but a plain positive integer is page 1. */
export function parsePage(raw: string | readonly string[] | undefined): number {
  return PageSchema.parse(raw);
}

/** The search param to write for `page`: page one clears the key. */
export const pageParam = (page: number): string | undefined =>
  page === 1 ? undefined : String(page);

const QUESTION: readonly (keyof FleetViewState)[] = ['filter', 'q', 'sort', 'dir', 'group', 'view'];

/** The page after the view moves from `prev` to `next`: one on a new question, else `page`. */
export function pageAfter(prev: FleetViewState, next: FleetViewState, page: number): number {
  return QUESTION.some((key) => prev[key] !== next[key]) ? 1 : page;
}

/**
 * The List view's page nav: `serverPageNav` over the list's total, six a page. It is the ONE
 * clamp: the `<Pager/>` draws `pageWindow` off this nav's page and page count, and
 * `listPage` slices off the same page, so the buttons and the rows can never disagree.
 */
export function listPageNav(o: {
  readonly list: () => MachineList;
  readonly page: () => number;
  readonly goTo: (page: number) => void;
}): ServerPageNav {
  return serverPageNav({
    page: o.page,
    total: () => o.list().total,
    pageSize: () => LIST_PAGE,
    goTo: o.goTo,
  });
}

/** The machines on `nav`'s page, by server group when grouped. */
export function listPage(list: MachineList, nav: PageNav): readonly PagedGroup[] {
  return pageGroups(list, LIST_PAGE, (nav.page() - 1) * LIST_PAGE);
}
